import { PUBLIC_API_URL } from '$env/static/public';
import { auth } from '$lib/auth/auth.svelte';
import type {
  BackendClient,
  BuildingWaypoint,
  DispatchTaskRequest,
  HealthFull,
  HealthPhase,
  QuotaInfo,
  RmfBuildingMap,
  RmfTask,
  RmfTaskDetail,
  RmfWebToken,
  SessionInfo,
  SessionStatus,
  StopResult,
  SwitchWorldResult,
  TaskStatus,
  WorldStatus
} from './backend-client';
import {
  BackendError,
  InvalidWorldError,
  MaintenanceModeError,
  QuotaExceededError,
  ServiceAtCapacityError,
  ServiceInitializingError,
  SessionNotFoundError,
  SwitchAlreadyInProgressError,
  UnauthorizedError
} from './errors';

type StartResponse = { dns: string; instance_id: string; state?: string };
type StopResponse = { duration_seconds: number; status?: string };
type StatusResponse = {
  state: 'no_session' | 'booting' | 'ready' | 'failed';
  instance_id: string | null;
  dns: string | null;
  elapsed_seconds: number | null;
  reason?: string;
};
type QuotaResponse = {
  used_seconds: number;
  limit_seconds: number;
  remaining_seconds: number;
  has_active_session: boolean;
  active_session_started_at: string | null;
  active_session_duration_seconds: number | null;
};
type RmfWebTokenResponse = { token: string; expires_in: number };
type HealthFullResponse = { phase: HealthPhase; phase_history?: Record<string, number> };

/** Raw vertex tel qu'expose dans level.nav_graphs[i].vertices. Tous les flags optionnels
 *  parce qu'un waypoint peut etre simple (sans is_charger, sans pickup) ou riche. */
type RawVertex = {
  name?: string;
  x: number;
  y: number;
  pickup_dispenser?: string;
  dropoff_ingestor?: string;
  is_charger?: boolean;
  is_holding_point?: boolean;
  is_parking_spot?: boolean;
};

/**
 * Raw building_map upstream rmf-web. Shape confirmee 2026-05-24 via capture console.
 *
 * Phase 8.10 : ce parser ne sert PLUS pour l'UI dispatch (qui utilise worlds-metadata.json,
 * cf doc backend Finding 1 : les semantic flags is_charger/is_cleaning_zone/pickup_dispenser/
 * dropoff_ingestor sont strippes a la serialisation Pydantic rmf-web). On garde le fetch
 * uniquement pour la readiness chain (savoir si l'endpoint est up) et le diagnostic console
 * (__tasks.buildingMap). Le retour est donc volontairement minimaliste.
 */
type RawBuildingMap = {
  name: string;
  levels: ReadonlyArray<{
    name: string;
    nav_graphs?: ReadonlyArray<{ vertices?: ReadonlyArray<RawVertex> }>;
  }>;
};

/** Raw task state upstream rmf-web. Shape complet beaucoup plus large ; on extrait juste les
 *  champs UI utiles (booking, status, assignment, timings, dispatch.errors pour debug). */
type RawTaskState = {
  booking: { id: string; unix_millis_request_time?: number };
  category?: string;
  status?: TaskStatus;
  assigned_to?: { group?: string; name?: string };
  unix_millis_start_time?: number;
  unix_millis_finish_time?: number;
  estimate_millis?: number;
  detail?: unknown;
  /** Erreurs de dispatch upstream rmf_task_msgs (ex "no fleet capable", "waypoint not in graph").
   *  Premier element typiquement le plus informatif. detail = message libre human-readable. */
  dispatch?: { errors?: { detail?: string }[] };
  /** Cle de phase active dans `phases`. Present uniquement sur GET /api/tasks/<id>/state
   *  (pas dans le listTasks paginated). Peut etre number ou string selon serialization rmf-web. */
  active?: string | number;
  /** Phases d'execution de la task (rmf_api.Phase). Chaque phase contient un dict d'events
   *  qui represente l'arbre d'execution concrete. L'event "underway" non-wrapper indique
   *  l'activite courante (go_to_place vs perform_action). */
  phases?: Record<
    string,
    {
      category?: string;
      events?: Record<string, RawEvent>;
    }
  >;
};

type RawEvent = {
  id: number;
  status: string;
  /** Nom human-readable de l'event. Sert au pattern matching (cf mapRawTaskDetail) parce que
   *  le backend ne pousse pas une category structuree au niveau event. Exemples observes :
   *  "Sequence", "Go to [place:clean_lobby]", "Perform action", "Pass through [door:L1_door5]",
   *  "Move to [place:...]", "Open [door:...]", "Close [door:...]". */
  name?: string;
  detail?: string;
  /** IDs des sous-events dependants. Sert a identifier les wrappers (events sans deps ou
   *  avec deps = sous-events terminaux). Pas utilise pour l'instant. */
  deps?: number[];
};

type ListTasksResponse = { items?: ReadonlyArray<RawTaskState> } | ReadonlyArray<RawTaskState>;
type WorldStatusResponse = {
  current_world: string;
  switching: boolean;
  target_world: string | null;
  expected_ready_at: string | null;
  last_error: string | null;
  allowed_worlds: readonly string[];
};
type SwitchWorldResponse = {
  target_world: string;
  noop?: boolean;
  estimated_duration_s: number;
  expected_ready_at: string;
};
type ErrorPayload = { code?: string; message?: string; used_seconds?: number; limit_seconds?: number };

/**
 * Client HTTP du plan de controle. Chaque call passe par auth.getValidAccessToken() qui gere
 * le refresh transparent (single-flight) si le token expire.
 */
export class RealBackendClient implements BackendClient {
  private static readonly DNS_KEY = 'rmf:backend:dns';
  private static readonly INSTANCE_KEY = 'rmf:backend:instance';
  private static readonly STARTED_AT_KEY = 'rmf:backend:started_at';

  private get storedDns(): string | null {
    if (typeof sessionStorage === 'undefined') return null;
    return sessionStorage.getItem(RealBackendClient.DNS_KEY);
  }

  private get storedStartedAt(): number {
    if (typeof sessionStorage === 'undefined') return 0;
    return Number(sessionStorage.getItem(RealBackendClient.STARTED_AT_KEY) ?? 0);
  }

  private writeSession(info: SessionInfo, startedAt: number) {
    sessionStorage.setItem(RealBackendClient.DNS_KEY, info.dns);
    sessionStorage.setItem(RealBackendClient.INSTANCE_KEY, info.instanceId);
    sessionStorage.setItem(RealBackendClient.STARTED_AT_KEY, String(startedAt));
  }

  private clearSession() {
    sessionStorage.removeItem(RealBackendClient.DNS_KEY);
    sessionStorage.removeItem(RealBackendClient.INSTANCE_KEY);
    sessionStorage.removeItem(RealBackendClient.STARTED_AT_KEY);
  }

  async start(): Promise<SessionInfo> {
    const token = await auth.getValidAccessToken();
    const resp = await fetch(`${PUBLIC_API_URL}/start`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!resp.ok) throw await mapError(resp);

    const data = (await resp.json()) as StartResponse;
    // Status 200 = entree DDB deja connue pour ce sub (idempotence ADR 020). 202 = creation
    // neuve. Attention : 200 ne garantit PAS que l'EC2 est up (cf F5 sur /welcome pendant le
    // boot). Le passage en phase=ready se fait uniquement via /status (sonde HTTPS publique
    // https://<dns>/health depuis AWS, avec validation du cert). wasExisting reste expose pour
    // l'observabilite mais ne pilote plus le flow.
    const wasExisting = resp.status === 200;
    const info: SessionInfo = { dns: data.dns, instanceId: data.instance_id, wasExisting };
    // Idempotence cote backend : meme sub -> meme DNS. Si on retrouve le meme DNS qu'avant,
    // on conserve le startedAt original pour ne pas reset l'attente sur F5 ou re-mount.
    const previousDns = this.storedDns;
    const startedAt = previousDns === info.dns && this.storedStartedAt > 0 ? this.storedStartedAt : Date.now();
    this.writeSession(info, startedAt);
    return info;
  }

  async getStatus(): Promise<SessionStatus> {
    const token = await auth.getValidAccessToken();
    const resp = await fetch(`${PUBLIC_API_URL}/status`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!resp.ok) throw await mapError(resp);
    const data = (await resp.json()) as StatusResponse;
    return {
      state: data.state,
      instanceId: data.instance_id,
      dns: data.dns,
      elapsedSeconds: data.elapsed_seconds,
      reason: data.reason
    };
  }

  async getHealthFull(): Promise<HealthFull> {
    const dns = this.storedDns;
    if (!dns) throw new SessionNotFoundError('No local session DNS for health/full');
    // No auth (cf scripts/e2e-rmf-full.sh T2 qui appelle sans Bearer). Timeout court 3s : si
    // rmf-web bloque le port pendant son lifespan FastAPI, ne pas accrocher tout le polling.
    const resp = await fetch(`https://${dns}/health/full`, {
      signal: AbortSignal.timeout(3000)
    });
    if (!resp.ok) throw await mapError(resp);
    const data = (await resp.json()) as HealthFullResponse;
    return { phase: data.phase, phaseHistory: data.phase_history };
  }

  async getFleetsCount(): Promise<number> {
    const dns = this.storedDns;
    if (!dns) throw new SessionNotFoundError('No local session DNS for fleets');
    const cognitoToken = await auth.getValidAccessToken();
    const resp = await fetch(`https://${dns}/api/fleets`, {
      headers: { Authorization: `Bearer ${cognitoToken}` }
    });
    if (!resp.ok) throw await mapError(resp);
    const data = (await resp.json()) as unknown;
    // rmf-web peut retourner soit un array brut, soit { items: [...] } selon la version
    // (cf listTasks plus haut). On supporte les deux pour rester robuste aux bumps upstream.
    if (Array.isArray(data)) return data.length;
    if (
      data !== null &&
      typeof data === 'object' &&
      'items' in data &&
      Array.isArray((data as { items: unknown[] }).items)
    ) {
      return (data as { items: unknown[] }).items.length;
    }
    return 0;
  }

  async heartbeat(): Promise<void> {
    const dns = this.storedDns;
    if (!dns) throw new SessionNotFoundError('No local session DNS to heartbeat');
    const token = await auth.getValidAccessToken();
    const resp = await fetch(`https://${dns}/heartbeat`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    });
    if (resp.ok) return; // 204 No Content attendu
    // 404 = session deja terminee cote backend (sweep, autre tab, /stop concurrent).
    // Le store consommateur va passer phase='lost' et arreter le heartbeat loop.
    if (resp.status === 404) {
      this.clearSession();
      throw new SessionNotFoundError();
    }
    throw await mapError(resp);
  }

  async stop(): Promise<StopResult> {
    const token = await auth.getValidAccessToken();
    const resp = await fetch(`${PUBLIC_API_URL}/stop`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!resp.ok) {
      // 404 session_not_found = cas non-fatal : la session a deja ete arretee (sweep, autre
      // tab, /stop precedent). On purge le state local et on retourne duration=0 pour que
      // l'UI ne traite pas ca comme une erreur bloquante.
      if (resp.status === 404) {
        this.clearSession();
        return { durationSeconds: 0 };
      }
      throw await mapError(resp);
    }
    const data = (await resp.json()) as StopResponse;
    this.clearSession();
    return { durationSeconds: data.duration_seconds };
  }

  async getQuota(): Promise<QuotaInfo> {
    const token = await auth.getValidAccessToken();
    const resp = await fetch(`${PUBLIC_API_URL}/quota`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!resp.ok) throw await mapError(resp);
    const data = (await resp.json()) as QuotaResponse;
    return {
      usedSeconds: data.used_seconds,
      limitSeconds: data.limit_seconds,
      remainingSeconds: data.remaining_seconds,
      hasActiveSession: data.has_active_session,
      activeSessionStartedAt: data.active_session_started_at,
      activeSessionDurationSeconds: data.active_session_duration_seconds
    };
  }

  async getWorldStatus(): Promise<WorldStatus> {
    // Pas d'auth (cf control-plane-api.md § /world-status) : le payload ne contient rien de
    // sensible (juste l'etat runtime du switch). Permet aussi de poller pendant la fenetre
    // ou le JWT trusted n'est pas encore obtenu (mount initial, refresh page).
    const dns = this.storedDns;
    if (!dns) throw new SessionNotFoundError('No local session DNS for world-status');
    const resp = await fetch(`https://${dns}/world-status`);
    if (!resp.ok) throw await mapError(resp);
    const data = (await resp.json()) as WorldStatusResponse;
    return {
      currentWorld: data.current_world,
      switching: data.switching,
      targetWorld: data.target_world,
      expectedReadyAt: data.expected_ready_at,
      lastError: data.last_error,
      allowedWorlds: data.allowed_worlds
    };
  }

  async switchWorld(world: string): Promise<SwitchWorldResult> {
    const dns = this.storedDns;
    if (!dns) throw new SessionNotFoundError('No local session DNS for switch-world');
    const token = await auth.getValidAccessToken();
    const resp = await fetch(`https://${dns}/switch-world`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ world })
    });
    // 409 : lock asyncio cote control_plane, un autre switch est en cours. Distingue d'un
    // simple BackendError pour que la UI propose le bon message ("attends la fin du switch
    // courant" plutot que "le backend est cassé").
    if (resp.status === 409) {
      let detail: ErrorPayload = {};
      try {
        detail = await resp.json();
      } catch {
        // payload vide accepte
      }
      throw new SwitchAlreadyInProgressError(detail.message);
    }
    // 400 : world hors allowlist (le selecteur UI devrait deja empecher ca).
    if (resp.status === 400) {
      let detail: ErrorPayload = {};
      try {
        detail = await resp.json();
      } catch {
        // payload vide accepte
      }
      throw new InvalidWorldError(detail.message);
    }
    if (!resp.ok) throw await mapError(resp);
    const data = (await resp.json()) as SwitchWorldResponse;
    return {
      targetWorld: data.target_world,
      noop: data.noop ?? false,
      estimatedDurationS: data.estimated_duration_s,
      expectedReadyAt: data.expected_ready_at
    };
  }

  async getRmfWebToken(): Promise<RmfWebToken> {
    // Cible le control_plane FastAPI cote EC2 (route /api/auth/rmf-web-token), PAS api.rmf.tf.
    // Le proxy `control_plane` recoit le Cognito Bearer ici, valide la signature (idem JWT
    // Authorizer API Gateway), puis renvoie un JWT HS256 trusted ephemere (~15 min) que le
    // frontend passera dans io({ auth: { token } }) et en Bearer sur les REST `/api/*` upstream.
    // Cf ADR 032 § "auth two-step".
    const dns = this.storedDns;
    if (!dns) throw new SessionNotFoundError('No local session DNS for rmf-web-token');
    const token = await auth.getValidAccessToken();
    const resp = await fetch(`https://${dns}/api/auth/rmf-web-token`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!resp.ok) throw await mapError(resp);
    const data = (await resp.json()) as RmfWebTokenResponse;
    return { token: data.token, expiresIn: data.expires_in };
  }

  async getBuildingMap(): Promise<RmfBuildingMap> {
    // GET /api/building_map traverse le proxy control_plane (swap Cognito -> trusted JWT).
    // Bearer Cognito requis comme pour les autres REST `/api/*`.
    const dns = this.storedDns;
    if (!dns) throw new SessionNotFoundError('No local session DNS for building_map');
    const cognitoToken = await auth.getValidAccessToken();
    const resp = await fetch(`https://${dns}/api/building_map`, {
      headers: { Authorization: `Bearer ${cognitoToken}` }
    });
    if (!resp.ok) throw await mapError(resp);
    const data = (await resp.json()) as RawBuildingMap;

    // Extraction minimaliste : nom + level + coords (les semantic flags is_charger/etc. sont
    // pas fiables upstream, cf doc backend Finding 1). Dedupe par name a travers nav_graphs.
    const byName = new Map<string, BuildingWaypoint>();
    for (const level of data.levels ?? []) {
      for (const graph of level.nav_graphs ?? []) {
        for (const v of graph.vertices ?? []) {
          if (!v.name) continue;
          if (byName.has(v.name)) continue;
          byName.set(v.name, {
            name: v.name,
            level: level.name,
            x: v.x,
            y: v.y,
            isCharger: !!v.is_charger,
            isHoldingPoint: !!v.is_holding_point,
            isParkingSpot: !!v.is_parking_spot,
            pickupDispenser: v.pickup_dispenser ?? null,
            dropoffIngestor: v.dropoff_ingestor ?? null
          });
        }
      }
    }
    return { name: data.name, waypoints: Array.from(byName.values()) };
  }

  async getTaskState(bookingId: string): Promise<RmfTaskDetail> {
    const dns = this.storedDns;
    if (!dns) throw new SessionNotFoundError('No local session DNS for task state');
    const cognitoToken = await auth.getValidAccessToken();
    const resp = await fetch(`https://${dns}/api/tasks/${encodeURIComponent(bookingId)}/state`, {
      headers: { Authorization: `Bearer ${cognitoToken}` }
    });
    if (!resp.ok) throw await mapError(resp);
    const data = (await resp.json()) as RawTaskState;
    return mapRawTaskDetail(data);
  }

  async listTasks(): Promise<RmfTask[]> {
    const dns = this.storedDns;
    if (!dns) throw new SessionNotFoundError('No local session DNS for tasks');
    const cognitoToken = await auth.getValidAccessToken();
    // L'API rmf-web `/api/tasks` est paginee ; on prend les 50 plus recentes (assez pour la
    // demo). Le tri par default est antichronologique. Si besoin de plus, ajouter `?limit=N`.
    const resp = await fetch(`https://${dns}/api/tasks?limit=50`, {
      headers: { Authorization: `Bearer ${cognitoToken}` }
    });
    if (!resp.ok) throw await mapError(resp);
    const data = (await resp.json()) as ListTasksResponse;
    // rmf-web peut retourner soit l'array directement, soit `{ items: [...] }` selon la version
    // (l'API a evolue). On supporte les deux pour ne pas se laisser pieger.
    const items: ReadonlyArray<RawTaskState> = Array.isArray(data)
      ? data
      : ((data as { items?: ReadonlyArray<RawTaskState> }).items ?? []);
    return items.map(mapRawTask);
  }

  async dispatchTask(
    fleetName: string | null,
    request: DispatchTaskRequest,
    labels?: readonly string[]
  ): Promise<RmfTask> {
    const dns = this.storedDns;
    if (!dns) throw new SessionNotFoundError('No local session DNS for dispatch');
    const cognitoToken = await auth.getValidAccessToken();
    const now = Date.now();
    // Schema rmf_api `dispatch_task_request` (cf rmf-task-dispatch.md).
    // Pieges importants :
    //   - patrol : category="patrol" direct + description={places, rounds}. OK.
    //   - delivery : category="delivery" direct + description={pickup, dropoff}. OK.
    //   - clean : DOIT utiliser category="compose" avec une description nested (sequence
    //     go_to_place + perform_action). Le format intuitif `{category:"clean", zone}` est
    //     accepte HTTP 200 mais le task_dispatcher upstream le rejette ensuite en silence via
    //     Socket.IO (task=failed). Cf doc backend Finding 3 + rmf_demos_tasks/dispatch_clean.py.
    // `fleet_name` est optionnel cote rmf_api_msgs/task_request.json. Si non-fourni, le
    // task_dispatcher RMF Core arbitre entre toutes les fleets capables via bidding.
    // `labels` propage le tag fonctionnel (ex `retry_of:OLD_TASK_ID`) pour tracabilite. rmf_api
    // labels est un array string libre, le backend le stocke tel quel sur le TaskState.
    const fleetNameField = fleetName ? { fleet_name: fleetName } : {};
    const labelsField = labels && labels.length > 0 ? { labels: [...labels] } : {};
    const payload =
      request.category === 'clean'
        ? buildComposeCleanPayload(fleetName, request, now, labels)
        : {
            type: 'dispatch_task_request',
            request: {
              unix_millis_earliest_start_time: now,
              unix_millis_request_time: now,
              requester: 'frontend-svelte',
              ...fleetNameField,
              ...labelsField,
              category: request.category,
              description: buildDescription(request)
            }
          };
    const resp = await fetch(`https://${dns}/api/tasks/dispatch_task`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${cognitoToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    if (!resp.ok) throw await mapError(resp);
    const data = (await resp.json()) as { state: RawTaskState };
    return mapRawTask(data.state);
  }

  async cancelTask(bookingId: string, labels?: readonly string[]): Promise<void> {
    const dns = this.storedDns;
    if (!dns) throw new SessionNotFoundError('No local session DNS for cancel');
    const cognitoToken = await auth.getValidAccessToken();
    // POST /api/tasks/cancel_task. Schema rmf_api_msgs/cancel_task_request : wrapper `type`
    // + champs flat task_id + labels (meme pattern que dispatch_task_request). Pre-Phase 8.46
    // bis on envoyait juste {task_id, labels} sans type -> 422 Pydantic validation. Cf backend
    // ticket § cancel.
    // Defaut labels=["user_cancelled"] : distinguer cote backend les annulations utilisateur
    // des futures annulations systeme (timeout, override admin) via TaskState.cancellation.labels.
    const effectiveLabels = labels && labels.length > 0 ? labels : ['user_cancelled'];
    const payload = {
      type: 'cancel_task_request',
      task_id: bookingId,
      labels: [...effectiveLabels]
    };
    const resp = await fetch(`https://${dns}/api/tasks/cancel_task`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${cognitoToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    if (!resp.ok) throw await mapError(resp);
    // Pas de body utile : le state passera a 'canceled' au prochain refresh listTasks.
  }

  cancel(): void {
    // Pas d'AbortController gere pour MVP-1.C : les fetches sont brefs (Lambda < 5 s typiquement).
    // Si la latence devient un sujet, stocker un AbortController par appel et l'abort ici.
  }
}

/** Conversion description camelCase typed → JSON snake_case attendu par rmf_api. Utilise pour
 *  patrol et delivery uniquement. Pour clean, voir buildComposeCleanPayload. */
function buildDescription(req: DispatchTaskRequest): unknown {
  if (req.category === 'patrol') {
    return { places: req.places, rounds: req.rounds };
  }
  if (req.category === 'delivery') {
    return {
      pickup: { place: req.pickup.place, handler: req.pickup.handler, payload: req.pickup.payload },
      dropoff: { place: req.dropoff.place, handler: req.dropoff.handler, payload: req.dropoff.payload }
    };
  }
  // Si clean arrive ici, c'est une erreur de routage : la branche compose aurait du etre
  // utilisee dans dispatchTask. Throw pour fail fast plutot que d'envoyer un payload qu'on
  // sait casse.
  throw new Error('buildDescription: clean must go through buildComposeCleanPayload');
}

/**
 * Payload pour clean : utilise un task compose avec sequence go_to_place + perform_action.
 * Le format intuitif `{category:"clean", zone}` est accepte HTTP 200 mais le task_dispatcher
 * le rejette en silence via Socket.IO. Cf gitlab-profile/docs/data/worlds-metadata.json
 * dispatch_examples.clean_hotel_compose pour le schema reference.
 *
 * Champs notables :
 *   - `category: "compose"` au niveau top (PAS "clean")
 *   - `description.category: "clean"` au niveau nested (informatif pour le dispatcher)
 *   - go_to_place puis perform_action : sequence des activites
 *   - expected_finish_location : le robot reste sur place apres clean
 *   - use_tool_sink: true : convention rmf_demos pour activer l'animation cleaning
 *   - unix_millis_action_duration_estimate: 60000 : 1 min estimee (juste indicatif pour
 *     l'UI backend, ne contraint pas la duree reelle)
 */
function buildComposeCleanPayload(
  fleetName: string | null,
  req: Extract<DispatchTaskRequest, { category: 'clean' }>,
  nowMs: number,
  labels?: readonly string[]
): unknown {
  return {
    type: 'dispatch_task_request',
    request: {
      unix_millis_earliest_start_time: nowMs,
      unix_millis_request_time: nowMs,
      requester: 'frontend-svelte',
      ...(fleetName ? { fleet_name: fleetName } : {}),
      ...(labels && labels.length > 0 ? { labels: [...labels] } : {}),
      category: 'compose',
      description: {
        category: 'clean',
        phases: [
          {
            activity: {
              category: 'sequence',
              description: {
                activities: [
                  { category: 'go_to_place', description: req.zone },
                  {
                    category: 'perform_action',
                    description: {
                      unix_millis_action_duration_estimate: 60000,
                      category: 'clean',
                      expected_finish_location: req.zone,
                      description: { zone: req.zone },
                      use_tool_sink: true
                    }
                  }
                ]
              }
            }
          }
        ]
      }
    }
  };
}

/** Mappe le RawTaskState upstream sur notre type minimaliste RmfTask. */
function mapRawTask(raw: RawTaskState): RmfTask {
  return {
    bookingId: raw.booking.id,
    // Normalisation lowercase de la category : le backend V2.x est inconsistent — il pousse
    // "Patrol" (capitalise) pour les patrols mais "clean" (lowercase) pour les compose-clean
    // qu'on envoie. Le frontend attend lowercase partout (i18n keys, gate `taskDetail.category
    // === 'patrol'` dans Robot.svelte). Normaliser ici masque cette inconsistance sans devoir
    // toucher tous les consommateurs. Cf incident 2026-05-28 : effet patrol jamais declenche
    // a cause de "Patrol" !== "patrol".
    category: (raw.category ?? 'unknown').toLowerCase(),
    status: raw.status ?? 'queued',
    assignedTo:
      raw.assigned_to && raw.assigned_to.name && raw.assigned_to.group
        ? { group: raw.assigned_to.group, name: raw.assigned_to.name }
        : null,
    requestedAtMillis: raw.booking.unix_millis_request_time ?? null,
    startedAtMillis: raw.unix_millis_start_time ?? null,
    finishedAtMillis: raw.unix_millis_finish_time ?? null,
    estimateMillis: raw.estimate_millis ?? null,
    failureReason: raw.dispatch?.errors?.[0]?.detail ?? null,
    detail: raw.detail
  };
}

/** Infere l'activite courante du robot depuis les events de la phase active. Cf backend
 *  ticket 2026-05-28 + capture empirique 2026-05-28 § phases.events.
 *
 *  Algorithme :
 *   1. Collecter les events `status === 'underway'`
 *   2. Filtrer les wrappers (`name === 'Sequence'`) qui sont toujours underway tant que
 *      la task tourne (pas une activite reelle)
 *   3. Matcher par prefixe du `name` : "Perform action" -> perform_action, "Go to" ->
 *      go_to_place. Le matching par prefixe absorbe les variations de detail entre
 *      versions backend (ex "Go to [place:clean_lobby]").
 *
 *  Return null si aucun event ne matche : task pas encore demarree, terminee, ou shape
 *  imprevu. Le frontend doit gerer null comme "pas d'activite identifiable".
 */
function inferCurrentActivity(raw: RawTaskState): 'go_to_place' | 'perform_action' | null {
  const activeKey = raw.active != null ? String(raw.active) : null;
  if (!activeKey || !raw.phases) return null;
  const phase = raw.phases[activeKey];
  if (!phase?.events) return null;
  const underway = Object.values(phase.events).filter((e) => e.status === 'underway');
  // Filtre wrappers Sequence : c'est l'envelope qui aggrege les sous-events, pas une activite.
  const realActivities = underway.filter((e) => e.name !== 'Sequence');
  // Pattern matching par prefixe du name. Ordre : perform_action prioritaire (= cleaning
  // effectif, ce qui declenche l'effet mousse). Si plusieurs underway, on prend le dernier
  // commence (heuristique : un perform_action n'est lance qu'apres que le go_to soit fini).
  if (realActivities.some((e) => e.name?.startsWith('Perform action'))) return 'perform_action';
  if (realActivities.some((e) => e.name?.startsWith('Go to'))) return 'go_to_place';
  return null;
}

/** Regex de parsing du `name` des events "Go to [place:NAME]". Capture le nom du waypoint
 *  entre crochets pour lookup ulterieur dans le building parsed. */
const GO_TO_PLACE_RE = /^Go to \[place:([^\]]+)\]/;

/** Extrait la liste des waypoints a venir depuis les events de la phase active. Solution C
 *  pour le tracé predictif sans dependance backend (cf backend ticket 2026-05-28 § path).
 *
 *  - Filtre les events `name.startsWith('Go to [place:')` qui ne sont PAS completed.
 *  - Trie par id event croissant = ordre d'execution attendu.
 *  - Parse le name pour extraire le waypoint name canonique (= cle de lookup dans
 *    building.levels[].waypoints).
 */
function extractUpcomingWaypoints(raw: RawTaskState): string[] {
  const activeKey = raw.active != null ? String(raw.active) : null;
  if (!activeKey || !raw.phases) return [];
  const phase = raw.phases[activeKey];
  if (!phase?.events) return [];
  const goTos = Object.values(phase.events)
    .filter((e) => e.status !== 'completed' && e.name?.startsWith('Go to [place:'))
    .sort((a, b) => a.id - b.id);
  const waypoints: string[] = [];
  for (const e of goTos) {
    const match = e.name?.match(GO_TO_PLACE_RE);
    if (match) waypoints.push(match[1]);
  }
  return waypoints;
}

/** Etend mapRawTask avec l'activite courante + les waypoints a venir, extraits des events. */
function mapRawTaskDetail(raw: RawTaskState): RmfTaskDetail {
  const base = mapRawTask(raw);
  return {
    ...base,
    currentActivity: inferCurrentActivity(raw),
    upcomingWaypoints: extractUpcomingWaypoints(raw)
  };
}

/** Mappe une reponse HTTP d'erreur sur la classe d'erreur typee correspondante. */
async function mapError(resp: Response): Promise<Error> {
  let payload: ErrorPayload = {};
  try {
    payload = await resp.json();
  } catch {
    // Pas de body JSON (gateway timeout, CORS preflight rejete, etc.) : on garde payload vide.
  }
  if (resp.status === 401) return new UnauthorizedError(payload.message);
  if (resp.status === 503) {
    if (payload.code === 'maintenance_mode') return new MaintenanceModeError(payload.message);
    if (payload.code === 'service_initializing') return new ServiceInitializingError(payload.message);
    if (payload.code === 'service_at_capacity') return new ServiceAtCapacityError(payload.message);
    return new BackendError(`Service unavailable: ${payload.message ?? '503'}`);
  }
  if (resp.status === 403 && payload.code === 'quota_exceeded') {
    return new QuotaExceededError({
      usedSeconds: payload.used_seconds ?? 0,
      limitSeconds: payload.limit_seconds ?? 0,
      message: payload.message
    });
  }
  if (resp.status === 404) return new SessionNotFoundError(payload.message);
  return new BackendError(`Backend error ${resp.status}: ${payload.message ?? resp.statusText}`);
}
